import React from 'react';

import { Field } from '@reckon-web/field';
import { Textarea } from '@reckon-web/textarea';
import { field, useForm, useSubmit, validate } from '@reckon-web/forms';

import { Dialog } from '../components/common/dialog';
import { useJobSignOffRevoke as _useJobSignOffRevoke } from '../data/useJobSignOffRevoke';

import { Job } from './use-query-job';
import { useDialog } from './use-dialog';

type JobSignOffRevokeDialogResult = { note: string };

const JobSignOffRevokeDialog: React.FC<{
  onClose: (res?: JobSignOffRevokeDialogResult) => void;
}> = ({ onClose }) => {
  const schema = field.object({
    note: field.multilineText({
      validate: validate((value) => {
        validate.required(value);
        return value;
      }),
    }),
  });

  const form = useForm(schema);

  const submit = useSubmit(form, ({ note }) => onClose({ note }));

  return (
    <Dialog
      title="Revoke approval of sign off request?"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, revoke"
      onSubmit={submit}
    >
      <Field
        label="Note"
        description="Add a note describing why you are revoking this approval"
      >
        <Textarea {...form.fields.note.props} />
      </Field>
    </Dialog>
  );
};

export const useJobSignOffRevoke = () => {
  const [_jobSignOffRevoke] = _useJobSignOffRevoke(); // TODO: handle loading and error states

  const [Dialog, openDialog] = useDialog();

  const jobSignOffRevoke = React.useCallback(
    async (job: Job) => {
      const res = await openDialog<JobSignOffRevokeDialogResult>(
        JobSignOffRevokeDialog,
        { size: 'small' }
      );
      if (!res) return;

      return _jobSignOffRevoke({
        variables: { cloudId: job.cloudId || '', note: res.note },
      });
    },
    [_jobSignOffRevoke, openDialog]
  );

  return React.useMemo(
    () =>
      [Dialog, jobSignOffRevoke] as [typeof Dialog, typeof jobSignOffRevoke],
    [Dialog, jobSignOffRevoke]
  );
};
